import { useLocation } from 'react-router-dom';
import React, { useEffect, useRef, useState } from 'react';
import { useDownloadPDF } from '../../hooks/useDownloadPDF.js';
import { useDownloadPNG } from '../../hooks/useDownloadPNG.js';
import { connect } from 'react-redux';
import { pushRoute } from '../../lib/router.js';
import { camelizeKeys } from 'humps';
import SubHeader from '../../components/SubHeader.jsx';
import PortfolioHeader from '../../components/headers/PageHeaders/Portfolio/PortfolioHeader.jsx';
import Empty from '../../components/Empty.jsx';
import { loadingContent } from '../../components/EmptyStates.jsx';
import PortfolioViewHeader from '../../components/headers/PageHeaders/Portfolio/PortfolioViewHeader.jsx';
import {
  deletePortfolio,
  retrievePortfolio,
  selectPortfolio,
  selectRetrievePortfolioState,
} from '../../modules/portfolio.js';
import {
  retrieveProjectStatuses,
  selectProjectStatusesWithArchived,
  selectRetrieveStatusesState,
} from '../../modules/projectStatuses.js';
import Timeline from '../../components/portfolio/Timeline/index.jsx';
import {
  deleteProject,
  retrieveIsShowAlertOnDelete,
  retrieveProjects,
  selectProjects,
  selectRetrieveProjectsState,
} from '../../modules/projects.js';
import ProjectsTable from '../../components/projects/ProjectsTable.jsx';
import PortfoliosActionDropdown from '../../components/portfolio/PortfoliosActionDropdown.jsx';
import { DeleteModal } from '../../components/modals/DeleteModal.jsx';
import EditMilestonesFormModal from '../../components/edit-milestones-form-modal/index.jsx';
import { getOrgTermsContent } from '../../content/utils.js';
import { selectOrganization } from '../../modules/organizations.js';
import { selectUserPermissions } from '../../modules/auth.js';
import { selectDuplicationsVersion } from '../../modules/projectDuplications.js';

const mapStateToProps = (state, ownProps) => {
  const retrieveSuccess = [
    selectRetrievePortfolioState,
    selectRetrieveStatusesState,
    selectRetrieveProjectsState,
  ].every((select) => select(state).success);

  return {
    portfolioId: ownProps.portfolioId,
    portfolio: selectPortfolio(ownProps.portfolioId)(state),
    projects: selectProjects(state),
    projectStatuses: selectProjectStatusesWithArchived(state),
    organization: selectOrganization(state),
    permissions: selectUserPermissions(state),
    duplicationsVersion: selectDuplicationsVersion(state),
    retrieveSuccess,
  };
};

const mapDispatchToProps = {
  retrievePortfolio,
  retrieveProjectStatuses,
  retrieveProjects,
  retrieveIsShowAlertOnDelete,
  deleteProject,
  deletePortfolio,
};

const PortfolioProjectsContainer = ({
  slug,
  portfolioId,
  portfolio,
  projects,
  retrievePortfolio,
  organization,
  projectStatuses,
  retrieveProjectStatuses,
  retrieveProjects,
  retrieveSuccess,
  retrieveIsShowAlertOnDelete,
  deleteProject,
  deletePortfolio,
  duplicationsVersion,
}) => {
  const location = useLocation();
  const route = location.pathname;

  const [showMilestonesModal, setShowMilestonesModal] = useState(false);
  const [milestones, setMilestones] = useState([]);
  const [ganttScaleType, setGanttScaleType] = useState('quarter');
  const { downloadPDF, loading: pdfLoading } = useDownloadPDF('ganttExportContainer', ganttScaleType);
  const { downloadPNG, loading: pngLoading } = useDownloadPNG('ganttExportContainer', ganttScaleType);
  const [deleteProjectState, setDeleteProjectState] = useState({
    showModal: false,
  });
  const portfolioProjectIds = portfolio
    ? portfolio.projects.map((p) => p.id)
    : [];
  const portfolioProjects = projects.filter((p) =>
    portfolioProjectIds.includes(p.id)
  );

  const onConfirmDeleteProject = (projectId) => {
    deleteProject(projectId).then(() => {
      retrievePortfolio(portfolioId);
      retrieveProjects();
      setDeleteProjectState({ showModal: false });
    });
  };

  const onDeleteProject = (projectId) => {
    retrieveIsShowAlertOnDelete(projectId).then(({ showAlert }) => {
      setDeleteProjectState({ showModal: showAlert });
      if (!showAlert) {
        window.confirm(
          `Delete project \n\n Are you sure you want to delete this ${getOrgTermsContent('project')}?`
        ) && onConfirmDeleteProject(projectId);
      }
    });
  };

  const onDeletePortfolio = (portfolioId) => {
    if (!window.confirm(getOrgTermsContent('deletePortfolioDescription')))
      return;
    deletePortfolio(portfolioId).then(() => pushRoute('projects/index'));
  };

  const onShowMilestoneModal = (milestones) => {
    setMilestones(milestones);
    setShowMilestonesModal(true);
  };

  const onHideMilestoneModal = () => {
    setMilestones([]);
    setShowMilestonesModal(false);
  };

  useEffect(() => {
    retrievePortfolio(portfolioId);
    retrieveProjects();
    retrieveProjectStatuses({ archived: true });
  }, []);

  // A duplication started or finished: its placeholder appeared, became a project or was removed
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    retrievePortfolio(portfolioId);
    retrieveProjects();
  }, [duplicationsVersion]);

  return (
    <React.Fragment>
      {!retrieveSuccess ? (
        <Empty simplified content={loadingContent} />
      ) : (
        <React.Fragment>
          <PortfolioHeader portfolio={portfolio} slug={slug} portfolioTabs />

          <SubHeader flexJustify small>
            <div className="flex">
              <h2>{portfolio.name}</h2>
            </div>
            <div className="flex flex-1 flex-end">
              <div className="flex">
                {portfolio.permissions ? (
                  <PortfoliosActionDropdown
                    slug={slug}
                    portfolioId={portfolioId}
                    portfolio={portfolio}
                    organization={organization}
                    onDeletePortfolio={onDeletePortfolio}
                    onDownloadPDF={downloadPDF}
                    pdfLoading={pdfLoading}
                    onDownloadPNG={downloadPNG}
                    pngLoading={pngLoading}
                  />
                ) : null}
              </div>
            </div>
          </SubHeader>
          <PortfolioViewHeader
            portfolio={portfolio}
            projectStatuses={projectStatuses}
          />

          <Timeline
            results={portfolioProjects}
            milestones={portfolio.milestones}
            onShowMilestoneModal={onShowMilestoneModal}
            onScaleTypeChange={setGanttScaleType}
          />

          <SubHeader flex small>
            <h2>{getOrgTermsContent('projects')}</h2>
            <strong className=" bordered text-muted m-l-16">
              Total:&nbsp;
              <span className="text-primary">{portfolioProjects.length}</span>
            </strong>
          </SubHeader>

          <ProjectsTable
            projects={portfolioProjects}
            statuses={projectStatuses}
            slug={slug}
            onDeleteProject={onDeleteProject}
          />
        </React.Fragment>
      )}

      <DeleteModal
        onDelete={onConfirmDeleteProject}
        show={deleteProjectState.showModal}
        onHide={() => setDeleteProjectState({ showModal: false })}
        message={
          'The project has initiatives, change impacts, or change impact tasks that are currently being tracked by participants or task owners. If you confirm the action, all related data will be lost. If you don\'t want to lose data for reporting, change the project status to "Archived"'
        }
        title="Delete project"
      />

      <EditMilestonesFormModal
        show={showMilestonesModal}
        onHide={onHideMilestoneModal}
        archived={false}
        entityKey="portfolios"
        canManage={
          portfolio &&
          portfolio.permissions &&
          camelizeKeys(portfolio.permissions).canManage
        }
        entities={milestones || []}
        onSubmitSuccess={() => retrievePortfolio(portfolioId)}
        onDeleteSuccess={() => retrievePortfolio(portfolioId)}
      />
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PortfolioProjectsContainer);
